import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { DealerDataService } from '../../dealer-data.service';
import { LightboxService } from '@core/services/lightbox.service';
import { TenantService } from '@core/tenant/tenant.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'ade-veiculo',
  templateUrl: './veiculo.component.html',
  styleUrls: ['./veiculo.component.scss'],
})
export class AdeVeiculoComponent implements OnInit, OnDestroy {
  vehicle: any = null;
  images: any[] = [];
  activeImage = 0;
  isLoading = true;
  descExpanded = false;
  showContact = false;

  private _unsub = new Subject<void>();

  constructor(
    private _route: ActivatedRoute,
    private _svc: DealerDataService,
    private _lightboxService: LightboxService,
    private _tenant: TenantService,
  ) {}

  get showPrices(): boolean {
    return this._tenant.showPrices;
  }

  ngOnInit(): void {
    const param = this._route.snapshot.paramMap.get('slug') ?? '';
    const id = param.split('-p').pop();
    if (!id) {
      this.isLoading = false;
      return;
    }

    this._svc
      .getProductById(id)
      .pipe(takeUntil(this._unsub))
      .subscribe({
        next: (r: any) => {
          const raw = Array.isArray(r?.data) ? r.data[0] : r?.data;
          this.vehicle = raw?.attributes ?? null;
          this.images = this.vehicle?.images?.data ?? [];
          this.isLoading = false;
          this._lightboxService.refresh();
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this._unsub.next();
    this._unsub.complete();
  }

  get name(): string {
    if (this.vehicle?.name) return this.vehicle.name;
    const brand =
      this.vehicle?.product_brand?.data?.attributes?.name ||
      this.vehicle?.customvalue_brand ||
      '';
    const model =
      this.vehicle?.product_model?.data?.attributes?.name ||
      this.vehicle?.customvalue_model ||
      '';
    return `${brand} ${model}`.trim();
  }

  get specs(): Array<{ label: string; value: string }> {
    const a = this.vehicle;
    if (!a) return [];
    const rows: Array<{ label: string; value: any }> = [
      {
        label: 'MARCA',
        value: a.product_brand?.data?.attributes?.name || a.customvalue_brand,
      },
      {
        label: 'MODELO',
        value: a.product_model?.data?.attributes?.name || a.customvalue_model,
      },
      { label: 'ANO', value: a.year },
      {
        label: 'CILINDRADA',
        value: a.cc
          ? `${new Intl.NumberFormat('pt-PT').format(a.cc)} cc`
          : null,
      },
      {
        label: 'PREÇO',
        value:
          this.showPrices && a.price
            ? `€ ${new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 }).format(a.price)}`
            : null,
      },
      { label: 'CONDIÇÃO', value: a.product_condition?.data?.attributes?.name },
      {
        label: 'QUILOMETRAGEM',
        value:
          a.quilometragem != null
            ? `${new Intl.NumberFormat('pt-PT').format(a.quilometragem)} km`
            : null,
      },
      {
        label: 'CERTIFICADO',
        value: a.isCertified != null ? (a.isCertified ? 'Sim' : 'Não') : null,
      },
      {
        label: 'IMPORTADO',
        value: a.isNacional != null ? (a.isNacional ? 'Não' : 'Sim') : null,
      },
    ];
    return rows.filter((r) => r.value != null && r.value !== '');
  }

  imgUrl(url: string): string {
    if (!url) return '';
    return url.startsWith('http') ? url : `${environment.assets}${url}`;
  }

  share(): void {
    if (navigator.share) {
      navigator.share({ title: this.name, url: window.location.href });
    } else {
      navigator.clipboard?.writeText(window.location.href);
    }
  }

  shareWhatsapp(): void {
    const text = `${this.name} - ${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  }

  shareEmail(): void {
    const subject = this.name;
    const body = window.location.href;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }
}
